/**
 * Client Portal Landing Page (/client/<token>).
 * R39 E4.b (SO-P274, mockup del-portal).
 *
 * One-time / persistent secure outside portal landing with SMS OTP verification.
 * Dispatches to subject-specific projector view (Delivery engagement now; Sites in G5).
 */

import React, { useEffect, useState, useCallback } from 'react'
import { useNls } from '../../nls/nlsProvider'
import {
  getClientLinkInfo,
  prepareClientLinkOtp,
  openClientLink,
} from '../../services/opsClientLinksService'
import { sendOutsidePhoneOtp } from '../../services/outsidePhoneOtp'
import { getApprovalsAuth, signOutApprovals } from '../../services/approvalsApp'
import type { ConfirmationResult } from 'firebase/auth'
import type {
  OpsClientLink,
  OpenClientLinkResult,
} from '../../types/ops'
import { Button } from '../Common/Button'
import { getClientViewRenderer } from './clientLinkRegistry'
import { classifyClientLinkError } from './clientLinkErrorHelper'
import './clientLinkLanding.css'

export interface ClientLinkLandingProps {
  token: string
}

type Step = 'LOADING' | 'INVALID' | 'LINK_INFO' | 'OTP' | 'PORTAL'
type InvalidReason = 'EXPIRED' | 'REVOKED' | 'NOT_FOUND' | 'SUBJECT_CLOSED' | 'GENERIC'

export function ClientLinkLanding({ token }: ClientLinkLandingProps): React.ReactElement {
  const { t } = useNls()

  const [step, setStep] = useState<Step>('LOADING')
  const [invalidReason, setInvalidReason] = useState<InvalidReason>('GENERIC')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<boolean>(false)

  // Step 1: Link info
  const [linkInfo, setLinkInfo] = useState<{
    workspaceName: string
    subjectTitle: string
    name: string
    phoneMasked: string
  } | null>(null)

  // Step 2: Phone input & OTP
  const [phone, setPhone] = useState<string>('')
  const [otpCode, setOtpCode] = useState<string>('')
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null)

  // Step 3: Portal view
  const [clientLink, setClientLink] = useState<OpsClientLink | null>(null)
  const [portalData, setPortalData] = useState<OpenClientLinkResult | null>(null)

  // 1. Initial link info fetch
  useEffect(() => {
    let cancelled = false

    async function loadLink() {
      if (!token) {
        setInvalidReason('NOT_FOUND')
        setStep('INVALID')
        return
      }

      try {
        const info = await getClientLinkInfo({ token })
        if (cancelled) return

        if (!info.workspaceName || !info.phoneMasked) {
          setInvalidReason('NOT_FOUND')
          setStep('INVALID')
          return
        }

        setLinkInfo(info)
        setStep('LINK_INFO')
      } catch (err: unknown) {
        if (cancelled) return
        const kind = classifyClientLinkError(err)
        if (kind === 'SUBJECT_CLOSED') {
          setInvalidReason('SUBJECT_CLOSED')
        } else if (kind === 'EXPIRED') {
          setInvalidReason('EXPIRED')
        } else if (kind === 'REVOKED') {
          setInvalidReason('REVOKED')
        } else {
          setInvalidReason('NOT_FOUND')
        }
        setStep('INVALID')
      }
    }

    void loadLink()

    return () => {
      cancelled = true
    }
  }, [token])

  // 2. Handle Send OTP with local last-4 check
  const handleSendOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (!linkInfo) return
    setError(null)

    const expectedLast4 = linkInfo.phoneMasked.replace(/\D/g, '').slice(-4)
    const enteredDigits = phone.replace(/\D/g, '')
    const enteredLast4 = enteredDigits.slice(-4)

    if (enteredLast4 !== expectedLast4 || enteredDigits.length < 4) {
      setError(t('NLSOpsClientPortalPhoneMismatch'))
      return
    }

    setBusy(true)
    try {
      const prep = await prepareClientLinkOtp({ token, last4: enteredLast4 })
      if (!prep.success || prep.attemptsRemaining <= 0) {
        setError(t('NLSOpsClientPortalOtpCapExhausted'))
        setBusy(false)
        return
      }

      const formattedPhone = phone.trim().startsWith('+') ? phone.trim() : `+${phone.trim()}`
      const confirmation = await sendOutsidePhoneOtp(getApprovalsAuth(), formattedPhone)
      setConfirmationResult(confirmation)
      setStep('OTP')
    } catch (err: unknown) {
      const kind = classifyClientLinkError(err)
      if (kind === 'RESOURCE_EXHAUSTED') {
        setError(t('NLSOpsClientPortalOtpCapExhausted'))
      } else {
        setError(t('NLSOpsClientPortalErrorGeneric'))
      }
    } finally {
      setBusy(false)
    }
  }

  // 3. Handle Verify OTP
  const handleVerifyOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (!confirmationResult) return
    setError(null)

    if (!otpCode || otpCode.trim().length !== 6) {
      setError(t('NLSOpsClientPortalInvalidOtp'))
      return
    }

    setBusy(true)
    try {
      await confirmationResult.confirm(otpCode.trim())
      const openRes = await openClientLink({ token })
      const renderer = getClientViewRenderer(openRes.subjectType || openRes.link?.subjectType)
      if (!renderer) {
        setInvalidReason('NOT_FOUND')
        setStep('INVALID')
        return
      }
      setClientLink(openRes.link)
      setPortalData(openRes)
      setStep('PORTAL')
    } catch (err: unknown) {
      const kind = classifyClientLinkError(err)
      if (kind === 'WRONG_OTP' || kind === 'OTP_EXPIRED') {
        setError(t('NLSOpsClientPortalInvalidOtp'))
      } else if (kind === 'PHONE_MISMATCH') {
        setError(t('NLSOpsClientPortalPhoneMismatch'))
      } else if (kind === 'SUBJECT_CLOSED') {
        setInvalidReason('SUBJECT_CLOSED')
        setStep('INVALID')
      } else {
        setError(t('NLSOpsClientPortalErrorGeneric'))
      }
    } finally {
      setBusy(false)
    }
  }

  // 4. Refresh portal data
  const handleRefresh = useCallback(async () => {
    setError(null)
    setBusy(true)
    try {
      const openRes = await openClientLink({ token })
      const renderer = getClientViewRenderer(openRes.subjectType || openRes.link?.subjectType)
      if (!renderer) {
        setInvalidReason('NOT_FOUND')
        setStep('INVALID')
        return
      }
      setClientLink(openRes.link)
      setPortalData(openRes)
    } catch {
      setError(t('NLSOpsClientPortalErrorGeneric'))
    } finally {
      setBusy(false)
    }
  }, [token, t])

  // 5. Handle Sign out
  const handleSignOut = async () => {
    setBusy(true)
    try {
      await signOutApprovals()
    } catch {
      // ignore
    } finally {
      setConfirmationResult(null)
      setOtpCode('')
      setClientLink(null)
      setPortalData(null)
      setStep('LINK_INFO')
      setBusy(false)
    }
  }

  // Render LOADING
  if (step === 'LOADING') {
    return (
      <div className="clientLink__container">
        <div className="clientLink__card clientLink__invalid">
          <div className="clientLink__title">{t('NLSOpsClientPortalLoading')}</div>
        </div>
      </div>
    )
  }

  // Render INVALID
  if (step === 'INVALID') {
    let msg = t('NLSOpsClientPortalLinkNotFound')
    if (invalidReason === 'SUBJECT_CLOSED') {
      msg = t('NLSOpsClientPortalStatusClosed')
    } else if (invalidReason === 'EXPIRED') {
      msg = t('NLSOpsClientPortalLinkExpired')
    } else if (invalidReason === 'REVOKED') {
      msg = t('NLSOpsClientPortalLinkRevoked')
    }

    return (
      <div className="clientLink__container" data-testid="client-link-invalid">
        <div className="clientLink__card clientLink__invalid">
          <div className="clientLink__title">{t('NLSOpsClientPortalTitle')}</div>
          <p className="clientLink__messageBody" data-testid="invalid-message">
            {msg}
          </p>
        </div>
      </div>
    )
  }

  // Render LINK_INFO
  if (step === 'LINK_INFO' && linkInfo) {
    return (
      <div className="clientLink__container" data-testid="client-link-info-step">
        <div className="clientLink__card">
          <div className="clientLink__header">
            <h1 className="clientLink__title">{t('NLSOpsClientPortalTitle')}</h1>
            <p className="clientLink__subtitle">{t('NLSOpsClientPortalSubtitle')}</p>
          </div>

          {error && (
            <div className="clientLink__errorBanner" role="alert">
              {error}
            </div>
          )}

          <div className="clientLink__infoGrid">
            <div className="clientLink__infoRow">
              <span className="clientLink__infoLabel">{t('NLSOpsClientPortalWorkspaceLabel')}</span>
              <span className="clientLink__infoValue">{linkInfo.workspaceName}</span>
            </div>
            <div className="clientLink__infoRow">
              <span className="clientLink__infoLabel">{t('NLSOpsClientPortalEngagementLabel')}</span>
              <span className="clientLink__infoValue">{linkInfo.subjectTitle}</span>
            </div>
            <div className="clientLink__infoRow">
              <span className="clientLink__infoLabel">{t('NLSOpsClientPortalApproverLabel')}</span>
              <span className="clientLink__infoValue">{linkInfo.name}</span>
            </div>
            <div className="clientLink__infoRow">
              <span className="clientLink__infoLabel">{t('NLSOpsClientPortalPhoneLabel')}</span>
              <span className="clientLink__infoValue">{linkInfo.phoneMasked}</span>
            </div>
          </div>

          <form onSubmit={handleSendOtp} className="clientLink__form">
            <div className="clientLink__field">
              <label htmlFor="client-phone-input" className="clientLink__label">
                {t('NLSOpsClientPortalPhoneVerifyTitle')}
              </label>
              <input
                id="client-phone-input"
                type="tel"
                className="clientLink__input"
                placeholder={t('NLSOpsClientPortalPhonePlaceholder')}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={busy}
                required
                data-testid="client-phone-input"
              />
            </div>

            <div className="clientLink__actions">
              <Button
                variant="primary"
                size="lg"
                type="submit"
                disabled={busy || phone.replace(/\D/g, '').length < 4}
                onClick={handleSendOtp}
                data-testid="send-otp-btn"
              >
                {busy ? t('NLSOpsClientPortalLoading') : t('NLSOpsClientPortalSendOtpBtn')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // Render OTP
  if (step === 'OTP') {
    return (
      <div className="clientLink__container" data-testid="client-link-otp-step">
        <div className="clientLink__card">
          <div className="clientLink__header">
            <h1 className="clientLink__title">{t('NLSOpsClientPortalOtpTitle')}</h1>
            <p className="clientLink__subtitle">{t('NLSOpsClientPortalOtpSubtitle')}</p>
          </div>

          {error && (
            <div className="clientLink__errorBanner" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleVerifyOtp} className="clientLink__form">
            <div className="clientLink__field">
              <label htmlFor="client-otp-input" className="clientLink__label">
                {t('NLSOpsClientPortalOtpTitle')}
              </label>
              <input
                id="client-otp-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                className="clientLink__input"
                placeholder="123456"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
                disabled={busy}
                autoFocus
                required
                data-testid="client-otp-input"
              />
            </div>

            <div className="clientLink__actions">
              <Button
                variant="primary"
                size="lg"
                type="submit"
                disabled={busy || otpCode.trim().length !== 6}
                onClick={handleVerifyOtp}
                data-testid="verify-otp-btn"
              >
                {busy ? t('NLSOpsClientPortalLoading') : t('NLSOpsClientPortalVerifyOtpBtn')}
              </Button>
              <Button
                variant="secondary"
                size="lg"
                type="button"
                disabled={busy}
                onClick={() => setStep('LINK_INFO')}
              >
                {t('NLSOpsDeliveryAllocationsEndAction')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // Render PORTAL
  const renderer = portalData ? getClientViewRenderer(portalData.subjectType || portalData.link?.subjectType) : undefined

  return (
    <div className="clientLink__container" data-testid="client-link-portal">
      <div className="clientLink__card">
        {renderer && portalData &&
          React.createElement(renderer, {
            token,
            view: portalData.view,
            link: clientLink,
            onRefresh: handleRefresh,
            onSignOut: handleSignOut,
          })}
      </div>
    </div>
  )
}
